// Lab catalog, reports and results (SPEC.md §4.3 Labs: one report = one date).
import { useDbQuery } from '../db/hooks';
import type { DbClient } from '../db/client';
import { classifyPanelTestValue, parseNumericLabValue, qualitativeStatus, type LabRange, type LabStatus } from '../lib/labs';
import { DEFAULT_LAB_TESTS, stdTestId, type CatalogTest } from './labCatalog';

export interface LabTest extends LabRange {
  id: string;
  /** Built-in tests only. */
  code: string | null;
  panel: string;
  name: string;
  unit: string | null;
  result_type: CatalogTest['result_type'];
  sort_order: number;
  is_custom: boolean;
  hidden: boolean;
  /** A built-in test whose unit or ranges the user has changed. */
  edited: boolean;
}

export interface LabTestRow extends LabRange {
  id: string;
  panel: string;
  name: string;
  code: string | null;
  unit: string | null;
  result_type: CatalogTest['result_type'];
  sort_order: number;
  is_custom: number;
  hidden: number;
}

/** Built-in tests with the user's changes applied, then the user's own tests. */
export function mergeCatalog(defaults: CatalogTest[], rows: LabTestRow[]): LabTest[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const builtIn = defaults.map((test): LabTest => {
    const id = stdTestId(test.code);
    const row = byId.get(id);
    const base = { ...test, id, is_custom: false, hidden: false, edited: false };
    if (!row) return base;
    return {
      ...base,
      unit: row.unit,
      normal_min: row.normal_min,
      normal_max: row.normal_max,
      critical_low: row.critical_low,
      critical_high: row.critical_high,
      hidden: row.hidden === 1,
      edited: true
    };
  });
  const custom = rows
    .filter((row) => row.is_custom === 1)
    .map((row): LabTest => ({ ...row, is_custom: true, hidden: row.hidden === 1, edited: false }));
  return [...builtIn, ...custom];
}

const TEST_COLUMNS = 'id, panel, name, code, unit, result_type, normal_min, normal_max, critical_low, critical_high, sort_order, is_custom, hidden';

export function useLabCatalog() {
  const query = useDbQuery<LabTestRow>(`SELECT ${TEST_COLUMNS} FROM lab_tests WHERE deleted_at IS NULL`, [], ['lab_tests']);
  return { tests: mergeCatalog(DEFAULT_LAB_TESTS, query.data ?? []), isLoading: query.isLoading };
}

type TestFields = Omit<LabTestRow, 'id' | 'is_custom'>;

/** Saves a test's unit, ranges or visibility. A built-in test keeps its id, so devices converge. */
export function saveTest(db: DbClient, test: LabTest, patch: Partial<Pick<LabTest, 'unit' | 'normal_min' | 'normal_max' | 'critical_low' | 'critical_high' | 'hidden' | 'name' | 'panel'>>) {
  const next = { ...test, ...patch };
  const values: TestFields & { is_custom: number } = {
    panel: next.panel,
    name: next.name,
    code: next.code,
    unit: next.unit,
    result_type: next.result_type,
    normal_min: next.normal_min,
    normal_max: next.normal_max,
    critical_low: next.critical_low,
    critical_high: next.critical_high,
    sort_order: next.sort_order,
    is_custom: next.is_custom ? 1 : 0,
    hidden: next.hidden ? 1 : 0
  };
  return db.upsert('lab_tests', test.id, values);
}

/** Back to the built-in unit and ranges. */
export function resetTest(db: DbClient, test: LabTest) {
  return db.softDelete('lab_tests', test.id);
}

export function addCustomTest(db: DbClient, values: Pick<LabTestRow, 'panel' | 'name' | 'unit' | 'result_type' | 'normal_min' | 'normal_max'>) {
  return db.insert('lab_tests', { ...values, is_custom: 1, sort_order: 1000, critical_low: null, critical_high: null });
}

export function deleteCustomTest(db: DbClient, test: LabTest) {
  return db.softDelete('lab_tests', test.id);
}

/* ------------------------------------------------------------------ Reports */

export interface LabReport {
  id: string;
  result_date: string;
  lab_name: string | null;
  notes: string | null;
}

export interface LabResult {
  id: string;
  report_id: string;
  test_id: string | null;
  test_name_snapshot: string;
  raw_value: string | null;
  numeric_value: number | null;
  unit: string | null;
  qualitative_value: string | null;
  status: LabStatus | null;
  /** From the report. */
  result_date: string;
}

export function useLabReports() {
  const query = useDbQuery<LabReport>('SELECT id, result_date, lab_name, notes FROM lab_reports WHERE deleted_at IS NULL ORDER BY result_date DESC', [], ['lab_reports']);
  return { reports: query.data ?? [], isLoading: query.isLoading };
}

/** Every result with its report date, oldest first. A person adds a few hundred a year. */
export function useLabResults() {
  const query = useDbQuery<LabResult>(
    `SELECT res.id, res.report_id, res.test_id, res.test_name_snapshot, res.raw_value, res.numeric_value, res.unit, res.qualitative_value, res.status, r.result_date
     FROM lab_results res JOIN lab_reports r ON r.id = res.report_id
     WHERE res.deleted_at IS NULL AND r.deleted_at IS NULL
     ORDER BY r.result_date, res.test_name_snapshot`,
    [],
    ['lab_results', 'lab_reports']
  );
  return { results: query.data ?? [], isLoading: query.isLoading };
}

/** The stored result for a typed value, flagged against the test's current range. */
export function resultFor(test: LabTest, raw: string) {
  const value = raw.trim();
  if (test.result_type === 'qualitative') {
    return { raw_value: value, numeric_value: null, qualitative_value: value, status: qualitativeStatus(value) };
  }
  if (test.result_type === 'text') return { raw_value: value, numeric_value: null, qualitative_value: null, status: 'unknown' as LabStatus };
  const numeric = parseNumericLabValue(value);
  return { raw_value: value, numeric_value: numeric, qualitative_value: null, status: classifyPanelTestValue(numeric, test) };
}

export type ReportFields = Pick<LabReport, 'result_date' | 'lab_name' | 'notes'>;

/**
 * Creates or updates a report and its results. `values` maps test id → typed value; an empty value
 * removes that result. Unchanged results aren't rewritten, so they don't sync again.
 */
export async function saveReport(db: DbClient, reportId: string | undefined, fields: ReportFields, values: Map<string, string>, tests: LabTest[], existing: LabResult[]) {
  const report = reportId ? await db.update<LabReport>('lab_reports', reportId, fields) : await db.insert<LabReport>('lab_reports', fields);
  const byTest = new Map(existing.map((result) => [result.test_id, result]));
  for (const test of tests) {
    const raw = values.get(test.id)?.trim() ?? '';
    const current = byTest.get(test.id);
    if (!raw) {
      if (current) await db.softDelete('lab_results', current.id);
      continue;
    }
    const computed = resultFor(test, raw);
    if (current && current.raw_value === computed.raw_value && current.status === computed.status && current.unit === test.unit) continue;
    const row = { ...computed, unit: test.unit, test_name_snapshot: test.name };
    if (current) await db.update('lab_results', current.id, row);
    else await db.insert('lab_results', { ...row, report_id: report.id, test_id: test.id });
  }
  return report;
}

export async function deleteReport(db: DbClient, reportId: string, results: LabResult[]) {
  for (const result of results) await db.softDelete('lab_results', result.id);
  await db.softDelete('lab_reports', reportId);
}
